import { useTranslation } from 'react-i18next';
import { FontWeight, Heading, LiliButton, LiliButtonType, Text } from '@lili/lili-frontend-common';
import { Icon, Icons } from '@Components/icon/Icon';
import logoUrl from '@Assets/logos/lili-logo-blue.svg';
import { DocVRequirementsList } from './DocVRequirementsList';
import { DocVLinkPanel } from './DocVLinkPanel';
import type { ReactNode } from 'react';

export interface DocVScreenProps {
  onSendSmsClick: () => void;
  openResendOption: boolean;
  timeLeft: string | null;
  isLoading: boolean;
  qrCode?: string;
  errorMessage?: string;
  onContinue: () => void;
  customDescription?: string;
  notification?: ReactNode;
}

export const DocVScreen = ({
  onSendSmsClick,
  openResendOption,
  timeLeft,
  isLoading,
  qrCode,
  errorMessage,
  onContinue,
  customDescription,
  notification,
}: DocVScreenProps) => {
  const { t } = useTranslation('nonUsSocure');

  return (
    <div
      data-testid='main-content-container'
      className='flex w-full h-full flex-col bg-surface-primary-bw-00'>
      <div className='flex w-full flex-1 flex-col items-center overflow-y-auto px-[16px] pt-[16px] pb-[24px] doc:px-0 doc:pt-[56px] doc:pb-[32px]'>
        <div className='flex w-full max-w-[480px] flex-col gap-[24px]'>
          {notification}

          <img src={logoUrl} alt='Lili logo' className='h-[44px] w-[44px]' />

          <div className='flex flex-col gap-[16px]'>
            <Heading.H5
              id='stepPageHeader'
              fontWeight={FontWeight.MEDIUM}
              className='text-[20px] leading-[26px] doc:max-w-[320px] doc:text-[24px] doc:leading-[32px]'>
              {t('pageContent.title')}
            </Heading.H5>

            {customDescription && (
              <Text.H10 className='text-text-main-secondary'>{customDescription}</Text.H10>
            )}

            <div className='flex flex-col gap-[16px] doc:gap-[40px]'>
              <DocVRequirementsList />

              <div className='flex flex-col gap-[24px]'>
                <DocVLinkPanel
                  isLoading={isLoading}
                  onSendSmsClick={onSendSmsClick}
                  openResendOption={openResendOption}
                  timeLeft={timeLeft}
                  qrCode={qrCode}
                />

                {errorMessage && !isLoading && (
                  <Text.H10 className='text-text-negative-red whitespace-pre-line'>
                    *{errorMessage}
                  </Text.H10>
                )}
              </div>

              <Text.H10 fontWeight={FontWeight.SEMIBOLD} className='doc:hidden'>
                {t('mainContent.requiredToApprove')}
              </Text.H10>
            </div>
          </div>
        </div>
      </div>

      <div className='w-full shrink-0 px-[16px] pb-[32px] pt-[16px] doc:hidden'>
        <LiliButton
          id='start-verification-process'
          type={LiliButtonType.PRIMARY}
          title={t('pageContent.mobileButtonTitle')}
          className='h-[54px] max-h-[54px]'
          suffixIcon={
            <Icon
              icon={Icons.Expand}
              className='h-[24px] w-[24px] text-text-main-primary-inverse'
            />
          }
          onClick={onContinue}
        />
      </div>
    </div>
  );
};
